import { useState } from "react";
import { useRooms } from "@/data/rooms";
import { useLang } from "@/lib/i18n";

const INK = "#e9dfcf";
const CENTERS = [95, 145, 195, 245];

const Walls = () => (
  <g stroke={INK} strokeWidth="1.3" fill="none" strokeLinecap="square">
    <rect x="0" y="0" width="300" height="60" strokeWidth="2.4" />
    {[[70, 87.2], [102.8, 137.2], [152.8, 187.2], [202.8, 237.2], [252.8, 270]].map(([a, b]) => (
      <line key={a} x1={a} y1="30" x2={b} y2="30" />
    ))}
    {[120, 170, 220].map((x) => <line key={x} x1={x} y1="0" x2={x} y2="30" />)}
    {[70, 270].map((x) => (
      <g key={x}>
        <line x1={x} y1="0" x2={x} y2="37.2" />
        <line x1={x} y1="52.8" x2={x} y2="60" />
      </g>
    ))}
    <g stroke="#E8B04A" strokeDasharray="1.4 1.4" strokeWidth="0.8">
      {CENTERS.map((c) => <path key={c} d={`M ${c - 7.8} 30 A 7.8 7.8 0 0 0 ${c + 7.8} 30`} />)}
      <path d="M 70 37.2 A 7.8 7.8 0 0 1 70 52.8" />
      <path d="M 270 37.2 A 7.8 7.8 0 0 0 270 52.8" />
    </g>
    <g strokeWidth="0.5" opacity="0.75">
      <rect x="1.5" y="1.5" width="12" height="11.5" strokeWidth="0.6" />
      {Array.from({ length: 9 }, (_, i) => <line key={i} x1="1.5" y1={13 + (i + 1) * 4.5} x2="13.5" y2={13 + (i + 1) * 4.5} />)}
      <path d="M 7.5 52 V 16 M 5.5 19 L 7.5 15.5 L 9.5 19" stroke="#7BC74D" strokeWidth="0.6" />
      <path d="M 13.5 6 A 8 8 0 0 1 21.5 14 L 13.5 14" strokeWidth="0.6" stroke="#E8B04A" />
      <rect x="42" y="1.5" width="21" height="6" rx="0.6" />
      <rect x="19" y="33" width="8.5" height="16" rx="1.2" />
      {[[40, 26], [57, 39]].map(([x, y]) => <circle key={`${x}-${y}`} cx={x} cy={y} r="3.8" />)}
      <line x1="23" y1="58.6" x2="49" y2="58.6" stroke="#7BC74D" strokeWidth="1.4" />
    </g>
  </g>
);

const Player = ({ p }) => (
  <g transform={`translate(${p.x * 10} ${p.z * 10}) rotate(${(-p.yaw * 180) / Math.PI})`} data-testid="plan-player">
    <path d="M 0 0 L -9 -18 A 20 20 0 0 1 9 -18 Z" fill="#7BC74D" opacity="0.28" />
    <circle r="3" fill="#7BC74D" stroke="#0d0c0a" strokeWidth="1" />
  </g>
);

const Label = ({ r }) => {
  const cx = r.label?.[0] ?? (r.rect.x + r.rect.w / 2) * 10;
  const cy = r.label?.[1] ?? (r.rect.z + r.rect.d / 2) * 10;
  return (
    <g pointerEvents="none" textAnchor="middle">
      <text x={cx} y={cy - 6} className="plan-num" fill={r.color}>{r.num}</text>
      <text x={cx} y={cy + 2} className="plan-name" fill={INK}>{r.name}</text>
      <text x={cx} y={cy + 8} className="plan-dims" fill="#b3aa9c">{r.dims}</text>
    </g>
  );
};

export const FloorPlan = ({ selected, onSelect, player, compact = false }) => {
  const [hover, setHover] = useState(null);
  const rooms = useRooms();
  const { t } = useLang();
  return (
    <svg viewBox="-4 -4 308 68" className="block h-auto w-full" data-testid={compact ? "minimap-plan" : "floor-plan"}>
      {!compact && <path d="M 22 14 H 286" className="plan-route" stroke="#7BC74D" strokeWidth="0.8" fill="none" />}
      {rooms.map((r) => {
        const on = r.id === selected || r.id === hover;
        return (
          <g
            key={r.id}
            role={compact ? undefined : "button"}
            tabIndex={compact ? -1 : 0}
            aria-label={r.name}
            data-testid={compact ? undefined : `plan-room-${r.id}`}
            className={compact ? "" : "cursor-pointer outline-none"}
            onClick={() => onSelect?.(r.id)}
            onKeyDown={(e) => e.key === "Enter" && onSelect?.(r.id)}
            onMouseEnter={() => !compact && setHover(r.id)}
            onMouseLeave={() => setHover(null)}
          >
            <rect
              x={r.rect.x * 10 + 1.5} y={r.rect.z * 10 + 1.5}
              width={(r.id === "social" ? 7 : r.rect.w) * 10 - 3} height={(r.id === "social" || r.id === "cineteca" ? 6 : 3) * 10 - 3}
              rx="1.5" fill={r.color} fillOpacity={r.id === selected ? 0.32 : on ? 0.18 : 0.04}
              stroke={r.color} strokeOpacity={on ? 0.9 : 0} strokeWidth="0.8"
              style={{ transition: "fill-opacity .35s ease, stroke-opacity .35s ease" }}
            />
            {!compact && <Label r={r} />}
          </g>
        );
      })}
      <g transform="translate(0 60) scale(1 -1)"><Walls /></g>
      {!compact && (
        <text x="170" y="9.5" textAnchor="middle" className="plan-dims" fill="#b3aa9c" pointerEvents="none">{t("plan.hall")}</text>
      )}
      {player && <Player p={player} />}
    </svg>
  );
};
